import { useMemo } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/person.pb';
import { PhoneEventsOrg } from '@wetags/phone-events';
import { AdaptoActions } from '@frontend/adapto-actions';
import { generateTrackingId } from '@frontend/call-intel-core';
import {
  CallBlockingCallbacks,
  CallRecordActionName,
  CallRecordActionProps,
  DeleteCallRecordingModal,
  getContactNumber,
  useDeleteCallRecording,
  useDownloadCallRecording,
  useCallRecordActionsGetter,
} from '@frontend/call-record-actions';
import { Icon } from '@frontend/icons';
import { BlockedIcon, BlockNumberModal, useBlockNumberFlow } from '@frontend/phone-ui-widgets';
import { Photos } from '@frontend/photos';
import { OnboardingHotspot, HOTSPOT_TARGET } from '@frontend/product-onboarding';
import { useAppScopeStore } from '@frontend/scope';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Text, useModalControl } from '@frontend/design-system';

type Contact = Required<Pick<Person, 'firstName' | 'lastName' | 'personId'>> & {
  profileName: string;
  number: string;
  defaultName: string;
  legalName: string;
};

type CallRecordActionsProps = {
  disableActions?: boolean;
  callRecord: HydratedCallRecord;
  hasDownloadAccess: boolean;
  hasDeleteAccess: boolean;
  onRecordingDeleted: () => void;
  callBlocking?: CallBlockingCallbacks;
};

type CallInsightContactSectionProps = {
  contact: Contact;
  locationId: string;
  callDirection: string;
} & Omit<CallRecordActionsProps, 'callRecord' | 'callBlocking'> & {
    callRecord: HydratedCallRecord | null;
  };

const PANEL_ACTION_PROPS: Partial<Record<CallRecordActionName, CallRecordActionProps>> = {
  call: { trackingIdSuffix: generateTrackingId({ prefix: 'call-insight-panel', context: 'call-contact' }) },
  message: {
    trackingIdSuffix: generateTrackingId({ prefix: 'call-insight-panel', context: 'message-contact' }),
    testId: 'message-contact',
  },
  viewProfile: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'view-profile' }) },
  createContact: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'create-contact' }) },
  downloadRecording: {
    trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'download-recording' }),
  },
  deleteRecording: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'delete-recording' }) },
  callBlocking: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'block-number' }) },
  callUnBlocking: { trackingId: generateTrackingId({ prefix: 'call-insight-panel', context: 'unblock-number' }) },
};

const getPanelActionProps = (action: CallRecordActionName): CallRecordActionProps | undefined =>
  PANEL_ACTION_PROPS[action];

const CallRecordActions = ({
  disableActions = false,
  callRecord,
  hasDownloadAccess,
  hasDeleteAccess,
  onRecordingDeleted,
  callBlocking,
}: CallRecordActionsProps) => {
  const deleteModalControls = useModalControl();

  const { downloadMedia } = useDownloadCallRecording();
  const { mutate: deleteMedia } = useDeleteCallRecording({
    onSuccess: onRecordingDeleted,
  });

  const getCallRecordActions = useCallRecordActionsGetter({
    downloadMedia: (record) => {
      downloadMedia({ channelId: record.channelId, locationId: record.locationId });
    },
    deleteMedia: () => {
      deleteModalControls.openModal();
    },
    hasDownloadAccess,
    hasDeleteAccess,
    disableMessageAction: disableActions,
    getActionProps: getPanelActionProps,
    callBlocking,
  });

  const callRecordActions = getCallRecordActions({
    index: 0,
    rowData: callRecord,
  });

  return (
    <>
      <section css={styles.actionsSection}>
        <AdaptoActions
          enableCloseEvent
          iconColor='inherit'
          variant='icon-buttons'
          maxActions={3}
          popoverPlacement='left'
          moreActionsTrackingId={generateTrackingId({ prefix: 'call-insight-panel', context: 'more-actions' })}
          css={{ display: 'flex' }}
        >
          {callRecordActions}
        </AdaptoActions>
      </section>
      <DeleteCallRecordingModal
        {...deleteModalControls.modalProps}
        onConfirm={() => deleteMedia({ channelId: callRecord.channelId, locationId: callRecord.locationId })}
      />
    </>
  );
};

export const CallInsightContactSection = ({
  contact,
  locationId,
  callDirection,
  disableActions,
  callRecord,
  hasDownloadAccess,
  hasDeleteAccess,
  onRecordingDeleted,
}: CallInsightContactSectionProps) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const { firstName, lastName, profileName, personId, number, defaultName, legalName } = contact;
  const isOutboundCall = callDirection === 'outbound';
  const isContactInteractive = !!personId && !disableActions;

  const [isCallBlockingEnabled] = PhoneEventsOrg.useWetag('call-blocking-from-call-history-and-insights');
  const { selectedLocationIds } = useAppScopeStore();
  const callLocationId = callRecord?.locationId ?? locationId;
  // Selected locations plus the call's own location, which may be outside the picker selection.
  // Needed if user opens panel from deeplink and the call's location is not part of the selected locations.
  const blockLocationIds = useMemo(
    () => Array.from(new Set([...selectedLocationIds, callLocationId].filter(Boolean))),
    [selectedLocationIds, callLocationId]
  );
  const {
    isBlocked,
    requestBlock,
    requestUnblock,
    modalProps: blockNumberModalProps,
    isCallBlockingFeatureUnavailable,
  } = useBlockNumberFlow({ locationIds: blockLocationIds });

  const callBlocking = useMemo<CallBlockingCallbacks | undefined>(
    () =>
      disableActions
        ? undefined
        : {
            isBlocked: (rowData) => isBlocked(getContactNumber(rowData), rowData.locationId),
            isUnavailable: isCallBlockingFeatureUnavailable,
            onBlock: (rowData) => {
              const phoneNumber = getContactNumber(rowData);
              if (phoneNumber) requestBlock(phoneNumber, rowData.locationId);
            },
            onUnblock: (rowData) => {
              const phoneNumber = getContactNumber(rowData);
              if (phoneNumber) requestUnblock(phoneNumber, rowData.locationId);
            },
          },
    [disableActions, isBlocked, isCallBlockingFeatureUnavailable, requestBlock, requestUnblock]
  );

  const showBlockedIcon =
    isCallBlockingEnabled &&
    !isCallBlockingFeatureUnavailable &&
    !!callRecord &&
    isBlocked(getContactNumber(callRecord), callRecord.locationId);

  const hasName = !!(firstName || lastName);

  return (
    <div css={styles.wrapper}>
      <div css={styles.row}>
        <OnboardingHotspot
          target={HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR}
          offset={{ top: 0, right: -4 }}
          enabled={isContactInteractive}
        >
          <Photos.ContactProfilePhoto
            css={styles.avatar}
            disableClick={!isContactInteractive}
            locationId={locationId}
            name={profileName || defaultName}
            personId={personId}
            title={legalName}
            data-testid='contact-profile-photo'
          />
        </OnboardingHotspot>

        <div css={styles.infoStack}>
          <div css={styles.titleRow}>
            {hasName ? (
              <div
                css={[styles.name, !isContactInteractive && styles.nameDisabled]}
                onClick={() => {
                  if (isContactInteractive) {
                    setPersonId(personId, undefined, locationId);
                    setShow(true, 'contact');
                  }
                }}
                data-testid='contact-name'
              >
                {!!firstName && (
                  <Text as='span' weight='bold'>
                    {firstName}
                  </Text>
                )}
                {!!lastName && <Text as='span'> {lastName}</Text>}
              </div>
            ) : (
              <Text as='span' color='light'>
                {number}
              </Text>
            )}
            {isOutboundCall && <Icon name='phone-outgoing-small' />}
            {!hasName && showBlockedIcon && <BlockedIcon size={16} />}
          </div>

          {hasName && (
            <div css={styles.numberRow}>
              <Text size='small' color='light'>
                {number}
              </Text>
              {showBlockedIcon && <BlockedIcon size={12} />}
            </div>
          )}
        </div>
      </div>
      {!!callRecord && (
        <CallRecordActions
          callRecord={callRecord}
          disableActions={disableActions}
          hasDownloadAccess={hasDownloadAccess}
          hasDeleteAccess={hasDeleteAccess}
          onRecordingDeleted={onRecordingDeleted}
          callBlocking={callBlocking}
        />
      )}
      {isCallBlockingEnabled && <BlockNumberModal {...blockNumberModalProps} />}
    </div>
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  row: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  avatar: css({
    flexShrink: 0,
  }),
  infoStack: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  }),
  name: css({
    cursor: 'pointer',
    width: 'fit-content',
  }),
  nameDisabled: css({
    cursor: 'default',
  }),
  titleRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  numberRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  }),
  actionsSection: css`
    display: flex;
    justify-content: center;
    gap: ${theme.spacing(1)};
  `,
  adaptoActions: css({
    display: 'flex',
  }),
};
